import { MemberPreferencesForUserV2 } from './types'
import { ProviderPreferenceTypeV2 } from '../../models/providers/Providers'
import { CountryNameToISOCode } from '../intl/constants'
import { MEMBER_PREFERENCE_LABELS } from '../searchForCare/messages'

export const CARE_LANGUAGES_PREFERENCE_CATEGORY = 'careLanguages'

export enum S4C_PROGRAM_OPTION_FLOW {
  THERAPY = 'THERAPY',
  COACHING = 'COACHING',
  COACHING_WITH_LMS = 'COACHING_WITH_LMS', // coaching with live messaging support
  SELF_INITIATED_TEENS = 'SELF_INITIATED_TEENS',
  PARENT_INITIATED_CHILD = 'PARENT_INITIATED_CHILD',
  PARENT_INITIATED_TEEN = 'PARENT_INITIATED_TEEN',
  OTHER = 'OTHER',
}

export enum MEETING_STYLE_MODAL_OPTIONS {
  NONE = 'NONE',
  IN_PERSON = 'IN_PERSON',
  VIDEO_AND_LIVE_MESSAGING = 'VIDEO_AND_LIVE_MESSAGING',
}

export enum MEMBER_PREFERENCE_IDS {
  // Race/ethnicity
  ASIAN = 'asian',
  // The below 3 are currently specific to Canada because they have a more granular breakdown than US/UK/Ireland.
  // For future countries with ethnicity preference added, use 'asian' unless there is a clear need to break it down further.
  EAST_ASIAN = 'eastAsian',
  SOUTH_ASIAN = 'southAsian',
  SOUTHEAST_ASIAN = 'southeastAsian',
  BLACK_OR_AFRICAN_AMERICAN = 'blackOrAfricanAmerican', // Kept for backwards compatibility but to be replaced by BLACK
  BLACK = 'black',
  HISPANIC_OR_LATINX = 'hispanicOrLatinx',
  NATIVE_AMERICAN_OR_ALASKA_NATIVE = 'nativeAmericanOrAlaskaNative',
  NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER = 'nativeHawaiianOrPacificIslander',
  WHITE = 'white',
  MIDDLE_EASTERN_OR_NORTH_AFRICAN = 'middleEasternOrNorthAfrican',
  INDIGENOUS = 'indigenous',
  // Gender
  MAN = 'man',
  WOMAN = 'woman',
  NON_BINARY_PERSON = 'nonBinaryPerson',
  // LGBTQIA+
  'LGBTQIA+' = 'LGBTQIA+',
  // Religion
  BUDDHIST = 'buddhist',
  CHRISTIAN = 'christian',
  HINDU = 'hindu',
  JEWISH = 'jewish',
  MORMON = 'mormon',
  MUSLIM = 'muslim',
  CLIENTS_OF_FAITH = 'clientsOfFaith',
  NON_RELIGIOUS_ATHEIST_AGNOSTIC = 'nonReligiousAtheistAgnostic',
  // Veterans
  EXPERIENCE_WORKING_WITH_VETERANS = 'experienceWorkingWithVeterans',
  // Meeting setting
  HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS = 'highlightProvidersWhoOfferInPersonSessions',
  VIDEO = 'video',
  LIVE_MESSAGING = 'live_messaging',
  // Availability
  FLEXIBLE = 'flexible',
  DAY_AND_TIME = 'dayAndTime',
  AVAILABILITIES = 'availabilities',
}

// Values from BE we receive for member preferences
// TODO: BE should be sending any matching values in their ID form and not label form
export const MEMBER_PREFERENCE_BE_TO_ID_MAPPING = Object.freeze({
  // Race/ethnicity
  Asian: MEMBER_PREFERENCE_IDS.ASIAN,
  'Asian or Asian American': MEMBER_PREFERENCE_IDS.ASIAN,
  'Black or African American': MEMBER_PREFERENCE_IDS.BLACK_OR_AFRICAN_AMERICAN,
  'Hispanic or Latino': MEMBER_PREFERENCE_IDS.HISPANIC_OR_LATINX,
  'Hispanic, Latino, or Spanish Origin': MEMBER_PREFERENCE_IDS.HISPANIC_OR_LATINX,
  'Middle Eastern or North African': MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN,
  'Native American or Alaska Native': MEMBER_PREFERENCE_IDS.NATIVE_AMERICAN_OR_ALASKA_NATIVE,
  'Native American or Alaskan Native': MEMBER_PREFERENCE_IDS.NATIVE_AMERICAN_OR_ALASKA_NATIVE,
  'American Indian or Alaskan Native': MEMBER_PREFERENCE_IDS.NATIVE_AMERICAN_OR_ALASKA_NATIVE,
  'American Indian or Alaska Native': MEMBER_PREFERENCE_IDS.NATIVE_AMERICAN_OR_ALASKA_NATIVE,
  'Native Hawaiian or Pacific Islander': MEMBER_PREFERENCE_IDS.NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER,
  'Native Hawaiian or Other Pacific Islander': MEMBER_PREFERENCE_IDS.NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER,
  White: MEMBER_PREFERENCE_IDS.WHITE,
  'CAN - Indigenous, First Nations': MEMBER_PREFERENCE_IDS.INDIGENOUS,
  'CAN - Middle Eastern, Arabic, or North African': MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN,
  'CAN - Black': MEMBER_PREFERENCE_IDS.BLACK,
  'CAN - Latin American': MEMBER_PREFERENCE_IDS.HISPANIC_OR_LATINX,
  'CAN - White, European, Caucasian': MEMBER_PREFERENCE_IDS.WHITE,
  'CAN - Asian, South': MEMBER_PREFERENCE_IDS.SOUTH_ASIAN,
  'CAN - Asian, Southeast': MEMBER_PREFERENCE_IDS.SOUTHEAST_ASIAN,
  'CAN - Asian, East': MEMBER_PREFERENCE_IDS.EAST_ASIAN,
  'IRL - White': MEMBER_PREFERENCE_IDS.WHITE,
  'IRL - Black, Black Irish, Caribbean or African': MEMBER_PREFERENCE_IDS.BLACK,
  'IRL - Asian or Asian Irish': MEMBER_PREFERENCE_IDS.ASIAN,
  'GBR - White': MEMBER_PREFERENCE_IDS.WHITE,
  'GBR - Black, Black British, Caribbean or African': MEMBER_PREFERENCE_IDS.BLACK,
  'GBR - Asian or Asian British': MEMBER_PREFERENCE_IDS.ASIAN,
  // Gender
  Man: MEMBER_PREFERENCE_IDS.MAN,
  Woman: MEMBER_PREFERENCE_IDS.WOMAN,
  'Non-binary': MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  'Gender non-conforming': MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  Agender: MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  Genderqueer: MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  'Two-spirit': MEMBER_PREFERENCE_IDS.NON_BINARY_PERSON,
  // Religion
  Buddhist: MEMBER_PREFERENCE_IDS.BUDDHIST,
  Christian: MEMBER_PREFERENCE_IDS.CHRISTIAN,
  Hindu: MEMBER_PREFERENCE_IDS.HINDU,
  Jewish: MEMBER_PREFERENCE_IDS.JEWISH,
  Mormon: MEMBER_PREFERENCE_IDS.MORMON,
  'Mormon (Church of Jesus Christ/LDS)': MEMBER_PREFERENCE_IDS.MORMON,
  Muslim: MEMBER_PREFERENCE_IDS.MUSLIM,
  'Non-religious/atheist/agnostic': MEMBER_PREFERENCE_IDS.NON_RELIGIOUS_ATHEIST_AGNOSTIC,
  // Meeting setting - GenAI API returns these exact values in preferredSessionFormat
  'In-Person': MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS,
  Video: MEMBER_PREFERENCE_IDS.VIDEO,
  'Live-Messaging': MEMBER_PREFERENCE_IDS.LIVE_MESSAGING,
})

export enum MEMBER_PREFERENCE_NAMES {
  ETHNICITIES = 'ethnicities',
  GENDER = 'gender',
  IS_LGBTQIA = 'isLgbtqia',
  RELIGIONS = 'religions',
  IS_EXPERIENCED_WITH_VETERANS = 'isExperiencedWithVeterans',
  MEETING_SETTING = 'meetingSetting',
  AVAILABILITIES = 'availabilities',
  PREFERRED_APPOINTMENT_TYPE = 'preferredAppointmentType',
  PREFERRED_SESSION_FORMAT = 'preferredSessionFormat',
}

export type ProviderPreferenceValues = {
  ethnicities: string[]
  gender: string[]
  isLgbtqia: string[]
  religions: string[]
  isExperiencedWithVeterans: string[]
  meetingSetting: string[]
  availabilities?: string[] | null
  timeZone?: string
  customFreeTextPreference?: string
}

// Convert MEMBER_PREFERENCE_ID into readable text for mixpanel events and simplified user preference mapping
export const MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING = Object.freeze({
  ethnicities: 'race/ethnicity',
  gender: 'gender',
  isLgbtqia: 'lgbtqia',
  religions: 'religion',
  isExperiencedWithVeterans: 'veterans',
  meetingSetting: 'meeting style',
  availabilities: 'availability',
})

export const MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL = Object.freeze({
  ...MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING,
  genderConservative: 'gender-conservative',
  isLgbtqiaConservative: 'lgbtqia-conservative',
})

// Conversion for Availability Calendar component mapping with time of day in military format
export const AVAILABILITY_PREFERENCE_TIME_OF_DAY_MAPPING: any = Object.freeze({
  0: '7-12', // 7AM - 12PM
  1: '12-17', // 12PM - 5PM
  2: '17-22', // 5PM - 10PM
})

// Conversion for Availability Calendar component mapping with day of week starting from integer 1 as Monday
export const AVAILABILITY_PREFERENCE_DAY_OF_WEEK_MAPPING: any = Object.freeze({
  0: 1, // Monday
  1: 2, // Tuesday
  2: 3, // Wednesday
  3: 4, // Thursday
  4: 5, // Friday
  5: 6, // Saturday
  6: 7, // Sunday
})

// Represents the initial empty RFF form state on the V2 ProviderPreferenceSelection page
export const emptyProviderPreferenceState = {
  ethnicities: [],
  gender: [],
  isLgbtqia: [],
  religions: [],
  isExperiencedWithVeterans: [],
  meetingSetting: [],
}

// Represents the initial empty RFF form state on the V3 ProviderPreferenceSelection page
export const emptyProviderPreferenceStateWithAvailability = {
  ethnicities: [],
  gender: [],
  isLgbtqia: [],
  religions: [],
  isExperiencedWithVeterans: [],
  availabilities: null,
  meetingSetting: [],
}

// Default / empty state (none selected) representation of the user's v2 preferences.
export const defaultV2PreferencesForUser: MemberPreferencesForUserV2 = {
  ethnicities: [],
  gender: [],
  isLgbtqia: false,
  religions: [],
  isBipoc: false,
  isExperiencedWithVeterans: false,
  preferredSessionFormat: [],
  preferredAppointmentType: [],
}

// Default / empty state for new availability preference
export const defaultAvailabilityPreferenceValues = {
  availabilities: null,
  timeZone: '',
}

// Empty state for parsed preferences from GenAI
export const emptyParsedPreference = {
  data: {},
  id: null,
  riskLevel: null,
}

// Default / empty state (none selected) representation of the user's preferences with availability properties
export const defaultV3PreferencesForUser: MemberPreferencesForUserV2 = {
  ethnicities: [],
  gender: [],
  isLgbtqia: false,
  religions: [],
  isBipoc: false,
  isExperiencedWithVeterans: false,
  preferredSessionFormat: [],
  preferredAppointmentType: [],
  availabilities: null,
  timeZone: '',
}

export const matchedPreferencesLabelsDefaults = {
  [ProviderPreferenceTypeV2.CARE_LANGUAGE]: MEMBER_PREFERENCE_LABELS.CARE_LANGUAGE,
  [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: {
    LGBTQIA_MATCH_IDENTITY: MEMBER_PREFERENCE_LABELS.LGBTQIA_MATCH_EXPERIENCE_WITH_COMMUNITY,
    LGBTQIA_MATCH_EXPERIENCE: MEMBER_PREFERENCE_LABELS.LGBTQIA_MATCH_EXPERIENCE_WITH_COMMUNITY,
  },
  [ProviderPreferenceTypeV2.EXPERIENCE_RELIGION]: {
    [MEMBER_PREFERENCE_IDS.BUDDHIST]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_BUDDHIST,
    [MEMBER_PREFERENCE_IDS.JEWISH]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_JEWISH,
    [MEMBER_PREFERENCE_IDS.CHRISTIAN]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_CHRISTIAN,
    [MEMBER_PREFERENCE_IDS.HINDU]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_HINDU,
    [MEMBER_PREFERENCE_IDS.MUSLIM]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_MUSLIM,
    [MEMBER_PREFERENCE_IDS.NON_RELIGIOUS_ATHEIST_AGNOSTIC]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_NON_RELIGIOUS,
    [MEMBER_PREFERENCE_IDS.CLIENTS_OF_FAITH]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_RELIGION_CLIENTS_OF_FAITH,
  },
  [ProviderPreferenceTypeV2.EXPERIENCE_VETERAN]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_VETERAN,
  [ProviderPreferenceTypeV2.AVAILABILITY]: {
    SPECIFIC_DAYS_AND_TIMES: MEMBER_PREFERENCE_LABELS.DaysAndTimes,
    SPECIFIC_DAY_AND_TIME: MEMBER_PREFERENCE_LABELS.DayAndTime,
  },
  [ProviderPreferenceTypeV2.SESSION_MODALITY]: {
    video: MEMBER_PREFERENCE_LABELS.video,
    live_messaging: MEMBER_PREFERENCE_LABELS.live_messaging,
    in_person: MEMBER_PREFERENCE_LABELS.inPerson,
  },
  [ProviderPreferenceTypeV2.GRANULAR_ETHNICITY]: {},
}

const sexualOrientationLabelsProgressive = {
  LGBTQIA_MATCH_IDENTITY: MEMBER_PREFERENCE_LABELS.LGBTQIA_MATCH_IDENTITY,
  LGBTQIA_MATCH_EXPERIENCE: MEMBER_PREFERENCE_LABELS.LGBTQIA_MATCH_EXPERIENCE,
}

export const matchedProviderPreferencesLabelsByCountry = {
  [CountryNameToISOCode.Australia]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.Brazil]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.Canada]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
    [ProviderPreferenceTypeV2.GRANULAR_ETHNICITY]: {
      [MEMBER_PREFERENCE_IDS.EAST_ASIAN]: MEMBER_PREFERENCE_LABELS.eastAsian,
      [MEMBER_PREFERENCE_IDS.SOUTH_ASIAN]: MEMBER_PREFERENCE_LABELS.southAsian,
      [MEMBER_PREFERENCE_IDS.SOUTHEAST_ASIAN]: MEMBER_PREFERENCE_LABELS.southeastAsian,
      [MEMBER_PREFERENCE_IDS.BLACK]: MEMBER_PREFERENCE_LABELS.black,
      [MEMBER_PREFERENCE_IDS.HISPANIC_OR_LATINX]: MEMBER_PREFERENCE_LABELS.latinAmerican,
      [MEMBER_PREFERENCE_IDS.WHITE]: MEMBER_PREFERENCE_LABELS.whiteEuropeanCaucasian,
      [MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN]:
        MEMBER_PREFERENCE_LABELS.middleEasternArabicOrNorthAfrican,
      [MEMBER_PREFERENCE_IDS.INDIGENOUS]: MEMBER_PREFERENCE_LABELS.indigenous,
    },
  },
  [CountryNameToISOCode.Ireland]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
    [ProviderPreferenceTypeV2.GRANULAR_ETHNICITY]: {
      [MEMBER_PREFERENCE_IDS.ASIAN]: MEMBER_PREFERENCE_LABELS.asian,
      [MEMBER_PREFERENCE_IDS.BLACK]: MEMBER_PREFERENCE_LABELS.black,
      [MEMBER_PREFERENCE_IDS.WHITE]: MEMBER_PREFERENCE_LABELS.white,
    },
  },
  [CountryNameToISOCode.Israel]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.EXPERIENCE_VETERAN]: MEMBER_PREFERENCE_LABELS.EXPERIENCE_MILITARY,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.Netherlands]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.NewZealand]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.SouthAfrica]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.Taiwan]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
  },
  [CountryNameToISOCode.UnitedKingdom]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
    [ProviderPreferenceTypeV2.GRANULAR_ETHNICITY]: {
      [MEMBER_PREFERENCE_IDS.ASIAN]: MEMBER_PREFERENCE_LABELS.asian,
      [MEMBER_PREFERENCE_IDS.BLACK]: MEMBER_PREFERENCE_LABELS.black,
      [MEMBER_PREFERENCE_IDS.WHITE]: MEMBER_PREFERENCE_LABELS.white,
    },
  },
  [CountryNameToISOCode.UnitedStatesOfAmerica]: {
    ...matchedPreferencesLabelsDefaults,
    [ProviderPreferenceTypeV2.SEXUAL_ORIENTATION]: sexualOrientationLabelsProgressive,
    [ProviderPreferenceTypeV2.GRANULAR_ETHNICITY]: {
      [MEMBER_PREFERENCE_IDS.ASIAN]: MEMBER_PREFERENCE_LABELS.asian,
      [MEMBER_PREFERENCE_IDS.BLACK_OR_AFRICAN_AMERICAN]: MEMBER_PREFERENCE_LABELS.blackOrAfricanAmerican,
      [MEMBER_PREFERENCE_IDS.BLACK]: MEMBER_PREFERENCE_LABELS.blackOrAfricanAmerican,
      [MEMBER_PREFERENCE_IDS.HISPANIC_OR_LATINX]: MEMBER_PREFERENCE_LABELS.hispanicOrLatinx,
      [MEMBER_PREFERENCE_IDS.NATIVE_AMERICAN_OR_ALASKA_NATIVE]: MEMBER_PREFERENCE_LABELS.nativeAmericanOrAlaskaNative,
      [MEMBER_PREFERENCE_IDS.NATIVE_HAWAIIAN_OR_PACIFIC_ISLANDER]:
        MEMBER_PREFERENCE_LABELS.nativeHawaiianOrPacificIslander,
      [MEMBER_PREFERENCE_IDS.WHITE]: MEMBER_PREFERENCE_LABELS.white,
      [MEMBER_PREFERENCE_IDS.MIDDLE_EASTERN_OR_NORTH_AFRICAN]: MEMBER_PREFERENCE_LABELS.middleEasternOrNorthAfrican,
    },
  },
}

export const providerPreferenceTypeList = [
  ProviderPreferenceTypeV2.GENDER,
  ProviderPreferenceTypeV2.GRANULAR_ETHNICITY,
  ProviderPreferenceTypeV2.SEXUAL_ORIENTATION,
  ProviderPreferenceTypeV2.EXPERIENCE_RELIGION,
  ProviderPreferenceTypeV2.EXPERIENCE_VETERAN,
  ProviderPreferenceTypeV2.AVAILABILITY,
  ProviderPreferenceTypeV2.SESSION_MODALITY,
  ProviderPreferenceTypeV2.ETHNICITY,
  ProviderPreferenceTypeV2.CARE_LANGUAGE,
]
